/**
 * Qty — a quantity with its unit of measure attached.
 *
 * A bare stock figure is ambiguous the moment a garage stocks anything that
 * is not counted in pieces: "0.5" against a paint tin means half a litre, and
 * against a brake pad it would mean half a pad. The number alone cannot say
 * which, and these figures are read in isolation — one cell in a column, one
 * card in a modal — so the unit has to travel with the number rather than sit
 * in a legend somewhere else on the screen.
 *
 * Deliberately shaped like Money: an inline dir="ltr" pair, value first, label
 * second, so the two read identically in Arabic and English and neither is
 * re-ordered by the bidi algorithm. The label is muted and slightly smaller —
 * the number is the data, the unit is the qualifier.
 *
 * unitLabel returns '' for an unknown unit (a part whose unit was never set),
 * in which case this renders exactly the bare number it always did.
 *
 * Usage: <Qty value={part.quantity_in_stock} unit={part.unit} />
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatQty, unitLabel } from '../../utils/qty';

export default function Qty({ value, unit, className = '' }) {
  const { t } = useTranslation();
  const label = unitLabel(t, unit);
  return (
    <span dir="ltr" className={`inline-flex items-baseline gap-1 whitespace-nowrap ${className}`}>
      <span className="tabular-nums">{formatQty(value, unit)}</span>
      {label && <span className="text-[0.8em] font-normal text-content-muted">{label}</span>}
    </span>
  );
}
